import React, { FC } from 'react';

import shield from 'assets/images/svg/capital-protection-logo.svg';
import protectionIcon from 'assets/images/svg/protection-black.svg';
import { ArrowIcon } from 'components/common/icons';
import { Image } from 'components/common/images';
import { useProfileData } from 'hooks';
import { cn, declension } from 'utils';

import styles from './CapitalProtectionBanner.module.scss';

export const CAPITAL_PROTECTION_REQUIRED_PROJECTS = 10;

const PROJECT_FORMS: [string, string, string] = ['проект', 'проекта', 'проектов'];

const projectsLabel = (count: number): string => `${count} ${declension(count, PROJECT_FORMS)}`;

type Props = {
  variant?: 'wide' | 'compact';
  className?: string;
};

const Promo: FC = () => (
  <div className={styles.promo}>
    <div className={styles.promoContent}>
      <div className={styles.promoTitle}>Вы участвуете в акции</div>
      <div className={styles.promoChips}>
        <span className={cn(styles.promoChip, styles.promoChipIcon)} aria-hidden="true">
          <ArrowIcon direction="right" size={20} title="" />
        </span>
        <span className={styles.promoChip}>акция Защита капитала</span>
      </div>
    </div>
    <Image className={styles.promoShield} src={shield} aria-hidden="true" />
  </div>
);

type ProgressTrackProps = {
  count: number;
};

const ProgressTrack: FC<ProgressTrackProps> = ({ count }) => {
  const steps = Array.from({ length: CAPITAL_PROTECTION_REQUIRED_PROJECTS }, (_, index) => index);
  const filledRatio =
    count > 0
      ? (Math.min(count, CAPITAL_PROTECTION_REQUIRED_PROJECTS) - 1) /
        (CAPITAL_PROTECTION_REQUIRED_PROJECTS - 1)
      : 0;

  return (
    <div className={styles.track}>
      <span className={styles.trackLine} />
      <span
        className={styles.trackLineFilled}
        style={{ transform: `scaleX(${filledRatio})` }}
        aria-hidden="true"
      />
      <div className={styles.trackDots}>
        {steps.map((index) => (
          <span
            key={index}
            className={cn(styles.trackDot, index < count && styles.trackDotFilled)}
          />
        ))}
      </div>
    </div>
  );
};

type ProgressRingProps = {
  count: number;
};

const RING_SIZE = 64;
const RING_STROKE = 5;

const ProgressRing: FC<ProgressRingProps> = ({ count }) => {
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.min(count / CAPITAL_PROTECTION_REQUIRED_PROJECTS, 1);

  return (
    <div className={styles.ring} style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
        <circle
          className={styles.ringTrack}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          strokeWidth={RING_STROKE}
        />
        <circle
          className={styles.ringValue}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          strokeWidth={RING_STROKE}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ratio)}
        />
      </svg>
      <span className={styles.ringLabel}>
        {count} / {CAPITAL_PROTECTION_REQUIRED_PROJECTS}
      </span>
    </div>
  );
};

const Success: FC = () => (
  <div className={styles.success}>
    <div className={styles.successTitle}>Поздравляем!</div>
    <p className={styles.successText}>
      Вы успешно собрали минимальное число проектов ({CAPITAL_PROTECTION_REQUIRED_PROJECTS}) для
      получения гарантии Защиты капитала в сделках, к которым вы применили Условия Акции
    </p>
    <p className={styles.successText}>
      Теперь к проектам со значком{' '}
      <Image className={styles.successIcon} src={protectionIcon} aria-hidden="true" /> из Мои
      инвестиции будут применяться <span className={styles.successAccent}>Условия Акции</span>
    </p>
  </div>
);

const CapitalProtectionBanner: FC<Props> = ({ variant = 'wide', className }) => {
  const { capitalProtectedInvestmentsCount } = useProfileData();

  if (capitalProtectedInvestmentsCount === null || capitalProtectedInvestmentsCount === undefined) {
    return null;
  }

  const count = Math.max(0, capitalProtectedInvestmentsCount);
  const completed = count >= CAPITAL_PROTECTION_REQUIRED_PROJECTS;
  const left = Math.max(0, CAPITAL_PROTECTION_REQUIRED_PROJECTS - count);

  return (
    <section
      className={cn(
        styles.banner,
        variant === 'compact' ? styles.bannerCompact : styles.bannerWide,
        className
      )}
    >
      <Promo />
      <div className={styles.body}>
        {completed ? (
          <Success />
        ) : variant === 'compact' ? (
          <div className={styles.compactStats}>
            <span className={cn(styles.label, styles.compactLabel)}>
              Проекты, проинвестированные в рамках Акции Защита капитала
            </span>
            <span className={cn(styles.value, styles.compactValue)}>{projectsLabel(left)}</span>
            <ProgressRing count={count} />
            <span className={cn(styles.label, styles.compactHint)}>
              Осталось проинвестировать для активации Акции
            </span>
          </div>
        ) : (
          <>
            <div className={styles.wideStats}>
              <div className={styles.wideColumn}>
                <span className={styles.label}>Проинвестировано в рамках Защиты капитала</span>
                <span className={styles.value}>{projectsLabel(count)}</span>
              </div>
              <div className={cn(styles.wideColumn, styles.wideColumnRight)}>
                <span className={styles.label}>До активации Защиты капитала</span>
                <span className={styles.value}>{projectsLabel(left)}</span>
              </div>
            </div>
            <ProgressTrack count={count} />
          </>
        )}
      </div>
    </section>
  );
};

export default CapitalProtectionBanner;
